/**
 * Shared entity route patterns and tab constants.
 * Single source of truth for URL construction — used by both
 * the MCP routeBuilder (backend) and toolDisplays (frontend).
 */

// ---------------------------------------------------------------------------
// Tab constants — single source of truth
// ---------------------------------------------------------------------------

export const INVESTMENT_TABS = [
  "overview",
  "analytics",
  "documents",
  "notes",
  "team",
  "gallery",
  "investment",
  "operations",
  "impact",
  "investments",
] as const;

export const FIRM_TABS = [
  "overview",
  "investments",
  "operations",
  "impact",
  "demographics",
  "team",
  "documents",
  "notes",
] as const;

export const COMPANY_TABS = [
  "overview",
  "deals",
  "investments",
  "demographics",
  "team",
  "documents",
  "notes",
] as const;

export const FIRM_ENGAGEMENT_TABS = [
  "overview",
  "notes",
  "documents",
] as const;

// ---------------------------------------------------------------------------
// Investment profile types — tabs vary by investment type
// ---------------------------------------------------------------------------

export type InvestmentProfileType = "PublicInvestment" | "PrivateInvestment" | "Portfolio";

/** Tabs available for public investments (always-visible, no permission gating) */
export const PUBLIC_INVESTMENT_TABS = [
  "overview", "analytics", "documents", "notes", "team", "gallery",
] as const;

/** Tabs available for private investments (always-visible, no permission gating) */
export const PRIVATE_INVESTMENT_TABS = [
  "overview", "analytics", "documents", "notes", "team", "gallery",
] as const;

/** Tabs available for portfolios (always-visible, no permission gating) */
export const PORTFOLIO_TABS = [
  "overview", "analytics", "investments", "documents",
] as const;

/** Subscriber-only tabs (require subscriberDataAccess flag) */
export const SUBSCRIBER_TABS = ["investment", "operations", "impact"] as const;

/** Lookup map: profile type -> base tabs */
export const INVESTMENT_PROFILE_TABS: Record<InvestmentProfileType, readonly string[]> = {
  PublicInvestment: PUBLIC_INVESTMENT_TABS,
  PrivateInvestment: PRIVATE_INVESTMENT_TABS,
  Portfolio: PORTFOLIO_TABS,
};

/**
 * Determine the investment profile type from isPortfolio flag and market ID.
 * - isPortfolio === true  -> "Portfolio"
 * - marketId === 2        -> "PrivateInvestment"
 * - else                  -> "PublicInvestment"
 */
export function getInvestmentProfileType(
  isPortfolio: boolean | null | undefined,
  marketId: number | null | undefined,
): InvestmentProfileType {
  if (isPortfolio) return "Portfolio";
  if (marketId === 2) return "PrivateInvestment";
  return "PublicInvestment";
}

// ---------------------------------------------------------------------------
// Derived union types
// ---------------------------------------------------------------------------

export type InvestmentTab = (typeof INVESTMENT_TABS)[number];
export type FirmTab = (typeof FIRM_TABS)[number];
export type CompanyTab = (typeof COMPANY_TABS)[number];
export type FirmEngagementTab = (typeof FIRM_ENGAGEMENT_TABS)[number];

// ---------------------------------------------------------------------------
// Entity types
// ---------------------------------------------------------------------------

export type EntityType =
  | "investment"
  | "firm"
  | "company"
  | "firmEngagement"
  | "knowledgeBase";
export type NavigableEntityType =
  | "investment"
  | "firm"
  | "company"
  | "firmEngagement";

// ---------------------------------------------------------------------------
// Tab map for runtime/generic access
// ---------------------------------------------------------------------------

export const ENTITY_TABS: Record<NavigableEntityType, readonly string[]> = {
  investment: INVESTMENT_TABS,
  firm: FIRM_TABS,
  company: COMPANY_TABS,
  firmEngagement: FIRM_ENGAGEMENT_TABS,
};

// ---------------------------------------------------------------------------
// Type-safe route builders
// ---------------------------------------------------------------------------

export const ENTITY_ROUTES = {
  investment: (id: number, tab?: InvestmentTab) =>
    `/investments/${id}${tab && tab !== "overview" ? `/${tab}` : ""}`,
  firm: (id: number, tab?: FirmTab) =>
    `/firms/${id}${tab && tab !== "overview" ? `/${tab}` : ""}`,
  company: (id: number, tab?: CompanyTab) =>
    `/company/${id}${tab && tab !== "overview" ? `/${tab}` : ""}`,
  firmEngagement: (id: number, tab?: FirmEngagementTab) =>
    `/engagements/${id}${tab && tab !== "overview" ? `/${tab}` : ""}`,
  knowledgeBase: (id?: number) =>
    `/knowledgeBase${id ? `?id=${id}` : ""}`,
};
